import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import { apiRequest, handleBranding } from '../../utils';

/* Company + teams setup, shown right after a team is created when a company is
   detected (or the event is company-only). Lets the captain link/create their
   company and spin up several named teams at once.
   Backend: GET/POST /api/team-setup. */
const TeamSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [view, setView] = useState('loading'); // confirmRole | confirmDomain | enterCompany | optional
  const [companyName, setCompanyName] = useState('');
  const [numTeams, setNumTeams] = useState(1);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        const data = await apiRequest('GET', '/team-setup');
        setInfo(data);
        if (data.team && data.team.company_id) { navigate('/team'); return; }
        if (data.detected && data.detected.source === 'role') setView('confirmRole');
        else if (data.detected && data.detected.source === 'domain') setView('confirmDomain');
        else if (data.company_only) setView('enterCompany');
        else setView('optional');
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
        setView('optional');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, t]);

  const post = async (body) => {
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', '/team-setup', body);
      navigate(data.redirect || '/team');
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  const link = () => post({ action: 'link', num_teams: numTeams });
  const create = () => {
    if (!companyName.trim()) { setError(t('companyNameRequired')); setShowToast(true); return; }
    post({ action: 'create', company_name: companyName.trim(), num_teams: numTeams });
  };
  const skip = () => post({ action: 'skip' });

  const detectedName = info && info.detected ? info.detected.name : null;
  const maxTeams = (info && info.max_teams) || 20;
  const companyOnly = info && info.company_only;

  const teamCount = (
    <div className="auth__field">
      <label className="auth__label">{t('howManyTeams')}</label>
      <div className="auth__input-wrap">
        <input
          className="auth__input"
          type="number"
          min="1"
          max={maxTeams}
          value={numTeams}
          onChange={(e) => setNumTeams(Math.max(1, Math.min(maxTeams, parseInt(e.target.value || '1', 10))))}
        />
      </div>
      <small style={{ color: '#6b7280', fontSize: '.78rem' }}>{t('teamsNamedHint')}</small>
    </div>
  );

  let title = t('setUpYourTeams');
  let subtitle = '';
  let body = null;

  if (view === 'confirmRole') {
    subtitle = t('linkCompanySubtitle');
    body = (
      <>
        <p style={{ marginTop: 0 }}>{t('detectedCompanyBody', { company: detectedName })}</p>
        {teamCount}
        <button className="auth__primary" onClick={link} disabled={submitting}>
          <Icon name="briefcase" /> {t('linkMyTeam')}
        </button>
        <button type="button" className="auth__forgot"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => setView('enterCompany')}>
          {t('differentCompany')}
        </button>
        {!companyOnly && (
          <button type="button" className="auth__forgot"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={skip} disabled={submitting}>
            {t('notNowPersonalTeam')}
          </button>
        )}
      </>
    );
  } else if (view === 'confirmDomain') {
    subtitle = t('linkCompanySubtitle');
    body = (
      <>
        <p style={{ marginTop: 0 }}>{t('areYouPartOf', { company: detectedName })}</p>
        {teamCount}
        <button className="auth__primary" onClick={link} disabled={submitting}>
          <Icon name="check" /> {t('yesLinkCompany')}
        </button>
        <button type="button" className="auth__forgot"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => setView('enterCompany')}>
          {t('noDifferentCompany')}
        </button>
      </>
    );
  } else if (view === 'enterCompany') {
    subtitle = companyOnly ? t('companyOnlySubtitle') : t('linkCompanySubtitle');
    body = (
      <>
        <div className="auth__field">
          <label className="auth__label">{t('whatCompany')}</label>
          <div className="auth__input-wrap">
            <input className="auth__input" type="text" value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={t('companyNamePlaceholder')} />
          </div>
        </div>
        {teamCount}
        <button className="auth__primary" onClick={create} disabled={submitting}>
          <Icon name="briefcase" /> {t('createAndLink')}
        </button>
        {!companyOnly && (
          <button type="button" className="auth__forgot"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={skip} disabled={submitting}>
            {t('notNowPersonalTeam')}
          </button>
        )}
      </>
    );
  } else { // optional
    subtitle = t('linkCompanySubtitle');
    body = (
      <>
        <p style={{ marginTop: 0 }}>{t('optionalCompanyBody')}</p>
        <button className="auth__primary" onClick={() => setView('enterCompany')} disabled={submitting}>
          <Icon name="briefcase" /> {t('addMyCompany')}
        </button>
        <button type="button" className="auth__forgot"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={skip} disabled={submitting}>
          {t('notNowPersonalTeam')}
        </button>
      </>
    );
  }

  return (
    <AuthShell title={title} subtitle={subtitle}>
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : (
        <div className="auth__card">
          {body}
          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </div>
      )}
    </AuthShell>
  );
};

export default TeamSetup;
